import React, { useState, useRef } from 'react';
import { useParams } from 'react-router-dom';
import { Camera, CheckCircle, AlertCircle, Plus } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { API } from '@/utils/api';
import axios from 'axios';

const ScanAttachmentPage = () => {
  const { sessionId } = useParams();
  const [status, setStatus] = useState('ready');
  const [uploadedCount, setUploadedCount] = useState(0);
  const [error, setError] = useState('');
  const fileRef = useRef(null);

  const handleCapture = async (e) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;

    setStatus('uploading');
    setError('');

    for (const file of Array.from(files)) {
      try {
        const formData = new FormData();
        formData.append('file', file);
        await axios.post(`${API}/attachment-scan-sessions/${sessionId}/upload`, formData, {
          headers: { 'Content-Type': 'multipart/form-data' },
        });
        setUploadedCount(prev => prev + 1);
      } catch (err) {
        setError(err.response?.data?.detail || 'Erreur lors de l\'envoi');
        setStatus('error');
        return;
      }
    }
    setStatus('success');
    e.target.value = '';
  };

  return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4">
      <div className="w-full max-w-sm space-y-6 text-center">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Vortekio.ch</h1>
          <p className="text-sm text-slate-500 mt-1">Envoyer des photos / fichiers</p>
        </div>

        {status === 'ready' && (
          <div
            className="bg-white rounded-2xl border-2 border-dashed border-slate-300 p-8 cursor-pointer hover:border-blue-400 transition-colors"
            onClick={() => fileRef.current?.click()}
            data-testid="capture-area"
          >
            <Camera className="w-16 h-16 mx-auto mb-4 text-blue-500" />
            <p className="text-lg font-semibold text-slate-700">Prendre une photo</p>
            <p className="text-sm text-slate-500 mt-1">ou choisir un fichier</p>
          </div>
        )}

        {status === 'uploading' && (
          <div className="bg-white rounded-2xl border border-slate-200 p-8">
            <div className="w-12 h-12 border-4 border-blue-500 border-t-transparent rounded-full animate-spin mx-auto mb-4" />
            <p className="text-lg font-semibold text-slate-700">Envoi en cours...</p>
          </div>
        )}

        {status === 'success' && (
          <div className="bg-white rounded-2xl border-2 border-emerald-300 p-8">
            <CheckCircle className="w-16 h-16 mx-auto mb-4 text-emerald-500" />
            <p className="text-lg font-semibold text-emerald-700">
              {uploadedCount} fichier(s) envoye(s)
            </p>
            <p className="text-sm text-slate-500 mt-1">Le fichier apparait sur l'ordinateur</p>
            <Button
              className="mt-4"
              onClick={() => { setStatus('ready'); fileRef.current?.click(); }}
              data-testid="add-more-btn"
            >
              <Plus className="w-4 h-4 mr-1" /> Ajouter d'autres photos
            </Button>
          </div>
        )}

        {status === 'error' && (
          <div className="bg-white rounded-2xl border-2 border-red-300 p-8">
            <AlertCircle className="w-16 h-16 mx-auto mb-4 text-red-500" />
            <p className="text-lg font-semibold text-red-700">Erreur</p>
            <p className="text-sm text-slate-500 mt-1">{error}</p>
            <Button className="mt-4" onClick={() => { setStatus('ready'); }}>Reessayer</Button>
          </div>
        )}

        <input
          ref={fileRef}
          type="file"
          accept="image/*,.pdf"
          capture="environment"
          multiple
          className="hidden"
          onChange={handleCapture}
          data-testid="file-input"
        />
      </div>
    </div>
  );
};

export default ScanAttachmentPage;
